'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowRight, Package, Layers, Users, QrCode, Download, Scissors } from 'lucide-react';
import { SiteNav } from '@/components/SiteNav';
import { Reveal, MarketingFooter } from '@/components/marketing';

export default function PricingContent() {
  const t = useTranslations('Landing');
  const tP = useTranslations('Pricing');
  const tS = useTranslations('Start');

  const FEATURES = [
    { Icon: Package,  fKey: 'f_stock' as const },
    { Icon: Layers,   fKey: 'f_catalog' as const },
    { Icon: Users,    fKey: 'f_team' as const },
    { Icon: QrCode,   fKey: 'f_qr' as const },
    { Icon: Download, fKey: 'f_export' as const },
    { Icon: Scissors, fKey: 'f_cut' as const },
  ];

  return (
    <div className="flex flex-col bg-stone-50 dark:bg-zinc-950 text-stone-950 dark:text-zinc-50">
      <SiteNav />

      {/* Hero */}
      <section style={{ padding: 'clamp(64px,9vw,120px) 0 clamp(32px,4vw,56px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
              style={{ color: 'var(--accent)' }}
            >
              05 / {t('pricingTeaser.eyebrow').split('/').pop()?.trim() ?? 'Piano'}
            </div>
            <h1
              className="font-black tracking-[-0.035em] leading-[1.05] m-0 mb-6 max-w-[18ch]"
              style={{ fontSize: 'clamp(40px,6vw,72px)' }}
            >
              {t('pricingTeaser.title')}
            </h1>
            <p className="text-[18px] leading-[1.6] text-stone-500 dark:text-zinc-400 max-w-[52ch] m-0">
              {t('pricingTeaser.body')}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Beta banner */}
      <section>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="flex items-start gap-4 p-5 rounded-[14px] border"
              style={{
                background: 'var(--accent-soft)',
                borderColor: 'color-mix(in srgb, var(--accent) 25%, transparent)',
              }}
            >
              <span
                className="shrink-0 font-mono text-[10px] font-bold tracking-[0.1em] px-2 py-0.5 rounded-[5px] mt-0.5"
                style={{ background: 'var(--accent)', color: '#fff' }}
              >
                {tP('trialBadge')}
              </span>
              <p className="text-[15px] leading-[1.55] m-0" style={{ color: 'var(--accent-strong)' }}>
                {tP('trialBody')}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Feature grid */}
      <section style={{ padding: 'clamp(48px,7vw,96px) 0' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="rounded-[20px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden"
            >
              {/* Card header */}
              <div className="px-8 py-7 border-b border-stone-200 dark:border-zinc-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div
                    className="font-mono text-[11px] font-semibold tracking-[0.12em] uppercase mb-1"
                    style={{ color: 'var(--accent)' }}
                  >
                    {t('pricingTeaser.cardBadge')}
                  </div>
                  <h2 className="text-[22px] font-bold tracking-tight m-0">{t('pricingTeaser.cardTitle')}</h2>
                  <p className="text-[14px] text-stone-500 dark:text-zinc-400 m-0 mt-1">{t('pricingTeaser.cardBody')}</p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="flex items-baseline gap-1.5 justify-end">
                    <span className="text-[36px] font-black tracking-tight leading-none" style={{ color: 'var(--accent)' }}>
                      {tP('priceAmount')}
                    </span>
                    <span className="text-[14px] font-medium text-stone-500 dark:text-zinc-400">
                      {tP('pricePeriod')}
                    </span>
                  </div>
                  <div className="text-[12px] text-stone-400 dark:text-zinc-500 mt-1">{tP('priceNote')}</div>
                </div>
              </div>

              {/* Feature list */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-stone-200 dark:divide-zinc-800">
                {FEATURES.map(({ Icon, fKey }) => (
                  <div key={fKey} className="flex items-start gap-3.5 px-6 py-5">
                    <div
                      className="shrink-0 w-8 h-8 rounded-[8px] flex items-center justify-center mt-0.5"
                      style={{ background: 'var(--accent-soft)' }}
                    >
                      <Icon size={16} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold text-stone-950 dark:text-zinc-50">{t(`pricingTeaser.${fKey}.title`)}</div>
                      <div className="text-[13px] text-stone-500 dark:text-zinc-400 mt-0.5 leading-[1.5]">{t(`pricingTeaser.${fKey}.desc`)}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA row */}
              <div className="px-8 py-6 border-t border-stone-200 dark:border-zinc-800 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link
                  href="/start"
                  className="inline-flex items-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity"
                >
                  {tP('ctaBtn')} <ArrowRight size={15} />
                </Link>
                <p className="text-[13px] text-stone-400 dark:text-zinc-500 m-0">
                  {tP('ctaBody')}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Le due formule: canone mensile oppure licenza una tantum piu' servizio */}
      <section style={{ padding: '0 0 clamp(48px,7vw,96px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <h2 className="text-[22px] font-bold tracking-tight mb-5">{tP('optionsTitle')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {([
                { k: 'sub', title: 'optionSubTitle', price: 'optionSubPrice', body: 'optionSubBody', href: '/signup', cta: 'subCta' },
                { k: 'lic', title: 'optionLicTitle', price: 'optionLicPrice', body: 'optionLicBody', href: '/licence', cta: 'licCta' },
              ] as const).map(({ k, title, price, body, href, cta }) => (
                <div
                  key={k}
                  className="rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 flex flex-col gap-2"
                >
                  <div
                    className="font-mono text-[11px] font-semibold tracking-[0.12em] uppercase"
                    style={{ color: 'var(--accent)' }}
                  >
                    {tP(title)}
                  </div>
                  <div className="text-[26px] font-black tracking-tight">{tP(price)}</div>
                  <p className="text-[14px] leading-[1.55] text-stone-500 dark:text-zinc-400 m-0">{tP(body)}</p>
                  {/* Le due formule non finiscono nello stesso posto: l'abbonamento si
                      attiva da soli, la licenza passa da una richiesta perche' il
                      software va installato sul server del cliente. */}
                  <Link
                    href={href}
                    className="mt-2 inline-flex items-center gap-1.5 text-[14px] font-semibold hover:gap-2.5 transition-all"
                    style={{ color: 'var(--accent)' }}
                  >
                    {tS(cta)} <ArrowRight size={14} />
                  </Link>
                </div>
              ))}
            </div>
            <p className="text-[13px] text-stone-400 dark:text-zinc-500 mt-4 m-0">{tP('quoteNote')}</p>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
